/**
 * @description
 * Stripe billing configuration settings component
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { HelpCircle, ExternalLink } from "lucide-react";
import { api } from "../../services/api";
import { StripeSettingsProps, StripeSettingsState } from "./types";

/**
 * Stripe settings component with self-contained state and logic
 */
export function StripeSettings({ onRefresh }: StripeSettingsProps) {
  const [stripeSettings, setStripeSettings] = useState<StripeSettingsState>({
    secretKey: "",
    publishableKey: "",
    webhookSecretSnapshot: "",
    webhookSecretThin: "",
    defaultThreshold: "100",
    defaultMargin: "100",
    alertEmail: "",
    autoCharge: true,
  });
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    loadStripeSettings();
  }, []);

  const loadStripeSettings = async () => {
    try {
      const settings = await api.getSettings();
      const stripeSettingsData = settings.filter(
        (s) => (s.category ?? "") === "stripe",
      );

      const newStripeSettings = {
        secretKey:
          stripeSettingsData.find((s) => s.key === "STRIPE_SECRET_KEY")
            ?.value || "",
        publishableKey:
          stripeSettingsData.find((s) => s.key === "STRIPE_PUBLISHABLE_KEY")
            ?.value || "",
        webhookSecretSnapshot:
          stripeSettingsData.find(
            (s) => s.key === "STRIPE_WEBHOOK_SECRET_SNAPSHOT",
          )?.value || "",
        webhookSecretThin:
          stripeSettingsData.find((s) => s.key === "STRIPE_WEBHOOK_SECRET_THIN")
            ?.value || "",
        defaultThreshold:
          stripeSettingsData.find((s) => s.key === "DEFAULT_USAGE_THRESHOLD")
            ?.value || "100",
        defaultMargin:
          stripeSettingsData.find((s) => s.key === "DEFAULT_MARGIN_PERCENTAGE")
            ?.value || "100",
        alertEmail:
          stripeSettingsData.find((s) => s.key === "THRESHOLD_ALERT_EMAIL")
            ?.value || "",
        autoCharge:
          stripeSettingsData.find((s) => s.key === "AUTO_CHARGE_ON_THRESHOLD")
            ?.value === "true",
      };

      setStripeSettings(newStripeSettings);
    } catch (error) {
      console.error("Error loading Stripe settings:", error);
      toast.error("Failed to load Stripe settings");
    }
  };

  const saveStripeSettings = async () => {
    try {
      setStripeLoading(true);

      const stripeSettingsToSave = [
        {
          key: "STRIPE_SECRET_KEY",
          value: stripeSettings.secretKey,
          category: "stripe",
          isSecret: true,
          description: "Stripe secret key for API authentication",
        },
        {
          key: "STRIPE_PUBLISHABLE_KEY",
          value: stripeSettings.publishableKey,
          category: "stripe",
          isSecret: false,
          description: "Stripe publishable key for client-side operations",
        },
        {
          key: "STRIPE_WEBHOOK_SECRET_SNAPSHOT",
          value: stripeSettings.webhookSecretSnapshot,
          category: "stripe",
          isSecret: true,
          description:
            "Stripe webhook signing secret for snapshot events (API v1)",
        },
        {
          key: "STRIPE_WEBHOOK_SECRET_THIN",
          value: stripeSettings.webhookSecretThin,
          category: "stripe",
          isSecret: true,
          description: "Stripe webhook signing secret for thin events (API v2)",
        },
        {
          key: "DEFAULT_USAGE_THRESHOLD",
          value: stripeSettings.defaultThreshold,
          category: "stripe",
          isSecret: false,
          description: "Default usage threshold amount in USD",
        },
        {
          key: "DEFAULT_MARGIN_PERCENTAGE",
          value: stripeSettings.defaultMargin,
          category: "stripe",
          isSecret: false,
          description:
            "Default margin percentage applied to costs (100% = double the cost)",
        },
        {
          key: "THRESHOLD_ALERT_EMAIL",
          value: stripeSettings.alertEmail,
          category: "stripe",
          isSecret: false,
          description: "Email address for billing alerts and notifications",
        },
        {
          key: "AUTO_CHARGE_ON_THRESHOLD",
          value: stripeSettings.autoCharge.toString(),
          category: "stripe",
          isSecret: false,
          description:
            "Automatically charge invoices when usage threshold is reached",
        },
      ];

      await api.updateSettings(stripeSettingsToSave);

      toast.success("Stripe settings saved successfully");
      onRefresh(); // Refresh parent settings
    } catch (error) {
      console.error("Error saving Stripe settings:", error);
      toast.error("Failed to save Stripe settings");
    } finally {
      setStripeLoading(false);
    }
  };

  const testStripeConnection = async () => {
    try {
      setStripeLoading(true);
      const result = await api.testStripeConnection();

      if (result.success) {
        toast.success("Stripe connection test successful");
      } else {
        toast.error(
          `Stripe connection test failed: ${result.error || "Unknown error"}`,
        );
      }
    } catch (error) {
      console.error("Error testing Stripe connection:", error);
      toast.error("Failed to test Stripe connection");
    } finally {
      setStripeLoading(false);
    }
  };

  const syncStripeProducts = async () => {
    try {
      setStripeLoading(true);
      const result = await api.syncStripeProducts();

      if (result.success) {
        toast.success(
          `Stripe products synced successfully (${result.productsSynced} products)`,
        );
      } else {
        toast.error(
          `Stripe product sync failed: ${result.error || "Unknown error"}`,
        );
      }
    } catch (error) {
      console.error("Error syncing Stripe products:", error);
      toast.error("Failed to sync Stripe products");
    } finally {
      setStripeLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stripe Settings Header with Help */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Stripe Billing Configuration</h3>
        <a
          href="/api/help/stripe"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          title="Open Stripe Billing Documentation"
        >
          <HelpCircle className="h-4 w-4" />
          <span>Help</span>
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {/* Stripe API Keys */}
      <div className="rounded-lg border p-4">
        <h3 className="text-md font-medium mb-3">Stripe API Configuration</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Secret Key</label>
            <p className="text-xs text-muted-foreground mt-1">
              Your Stripe secret key (starts with sk_)
            </p>
            <input
              type="password"
              value={stripeSettings.secretKey}
              onChange={(e) =>
                setStripeSettings((prev) => ({
                  ...prev,
                  secretKey: e.target.value,
                }))
              }
              placeholder="sk_test_..."
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Publishable Key</label>
            <p className="text-xs text-muted-foreground mt-1">
              Your Stripe publishable key (starts with pk_)
            </p>
            <input
              type="text"
              value={stripeSettings.publishableKey}
              onChange={(e) =>
                setStripeSettings((prev) => ({
                  ...prev,
                  publishableKey: e.target.value,
                }))
              }
              placeholder="pk_test_..."
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-sm font-medium">
              Webhook Secret - Snapshot Events (API v1)
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Signing secret for snapshot events endpoint (starts with whsec_)
            </p>
            <input
              type="password"
              value={stripeSettings.webhookSecretSnapshot}
              onChange={(e) =>
                setStripeSettings((prev) => ({
                  ...prev,
                  webhookSecretSnapshot: e.target.value,
                }))
              }
              placeholder="whsec_..."
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-sm font-medium">
              Webhook Secret - Thin Events (API v2)
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Signing secret for thin events endpoint (starts with whsec_)
            </p>
            <input
              type="password"
              value={stripeSettings.webhookSecretThin}
              onChange={(e) =>
                setStripeSettings((prev) => ({
                  ...prev,
                  webhookSecretThin: e.target.value,
                }))
              }
              placeholder="whsec_..."
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Webhook Endpoint Information */}
      <div className="rounded-lg border p-4 bg-blue-50/50 border-blue-200">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-blue-100 p-2">
            <svg
              className="h-5 w-5 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">
              Webhook Endpoint Configuration
            </h4>
            <p className="text-sm text-blue-700 mb-3">
              Stripe supports two event formats. You need to create TWO separate
              webhook endpoints in your Stripe Dashboard.
            </p>

            {/* Snapshot Events Endpoint */}
            <div className="bg-white rounded-md p-3 border border-blue-100 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-blue-900 bg-blue-100 px-2 py-1 rounded">
                  Snapshot Events (API v1)
                </span>
              </div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">
                Webhook URL
              </label>
              <div className="flex items-center gap-2 mb-2">
                <code className="flex-1 text-sm bg-gray-50 px-3 py-2 rounded border font-mono">
                  {window.location.origin}/api/stripe/webhooks
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${window.location.origin}/api/stripe/webhooks`,
                    );
                    toast.success("Snapshot webhook URL copied");
                  }}
                  className="rounded-md border border-input bg-background px-3 py-2 text-xs font-medium transition-colors hover:bg-accent"
                  title="Copy to clipboard"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-blue-600">
                <strong>Configuration:</strong> Payload style:{" "}
                <strong>Snapshot</strong> | API version:{" "}
                <strong>2025-07-30.basil</strong> (or your preferred version)
              </p>
            </div>

            {/* Thin Events Endpoint */}
            <div className="bg-white rounded-md p-3 border border-blue-100 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-purple-900 bg-purple-100 px-2 py-1 rounded">
                  Thin Events (API v2)
                </span>
              </div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">
                Webhook URL
              </label>
              <div className="flex items-center gap-2 mb-2">
                <code className="flex-1 text-sm bg-gray-50 px-3 py-2 rounded border font-mono">
                  {window.location.origin}/api/stripe/webhooks/
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${window.location.origin}/api/stripe/webhooks/`,
                    );
                    toast.success("Thin events webhook URL copied");
                  }}
                  className="rounded-md border border-input bg-background px-3 py-2 text-xs font-medium transition-colors hover:bg-accent"
                  title="Copy to clipboard"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-purple-600">
                <strong>Configuration:</strong> Payload style:{" "}
                <strong>Thin</strong> | API version:{" "}
                <strong>Unversioned</strong>
              </p>
            </div>

            <div className="mt-3 text-xs text-blue-600 bg-blue-50 p-3 rounded">
              <p className="font-medium mb-2">📋 Setup Instructions:</p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>
                  Go to{" "}
                  <a
                    href="https://dashboard.stripe.com/webhooks"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-blue-800 font-medium"
                  >
                    Stripe Dashboard → Webhooks
                  </a>
                </li>
                <li>
                  <strong>Create First Endpoint (Snapshot):</strong>
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5">
                    <li>Click "Add endpoint" and paste the first URL above</li>
                    <li>Choose "Snapshot" as payload style</li>
                    <li>Select API version (e.g., 2025-07-30.basil)</li>
                    <li>Select events to listen for</li>
                    <li>
                      Copy the signing secret and paste it in "Webhook Secret -
                      Snapshot Events" field above
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>Create Second Endpoint (Thin):</strong>
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5">
                    <li>
                      Click "Add endpoint" again and paste the second URL above
                    </li>
                    <li>Choose "Thin" as payload style</li>
                    <li>API version will be "Unversioned"</li>
                    <li>Select events to listen for</li>
                    <li>
                      Copy the signing secret and paste it in "Webhook Secret -
                      Thin Events" field above
                    </li>
                  </ul>
                </li>
                <li>Save your configuration on this page</li>
              </ol>
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <p className="font-medium text-yellow-800">
                  💡 Why two endpoints?
                </p>
                <p className="text-yellow-700 mt-1">
                  <strong>Snapshot events</strong> include complete object data
                  but can be stale.
                  <strong>Thin events</strong> are lightweight notifications
                  that guarantee fresh data when you fetch it. Using both
                  provides flexibility for different integration needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stripe API Keys - Remove duplicate closing div */}
      <div className="rounded-lg border p-4">
        <h3 className="text-md font-medium mb-3">Additional Settings</h3>
        <div className="space-y-4">{/* Moved content here if needed */}</div>
      </div>

      {/* Default Billing Settings */}
      <div className="rounded-lg border p-4">
        <h3 className="text-md font-medium mb-3">Default Billing Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">
              Default Usage Threshold
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Default threshold amount in USD for usage-based billing
            </p>
            <select
              value={stripeSettings.defaultThreshold}
              onChange={(e) =>
                setStripeSettings((prev) => ({
                  ...prev,
                  defaultThreshold: e.target.value,
                }))
              }
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="100">$100</option>
              <option value="500">$500</option>
              <option value="1000">$1000</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">
              Default Margin Percentage
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Default markup percentage applied to costs
            </p>
            <input
              type="number"
              value={stripeSettings.defaultMargin}
              onChange={(e) =>
                setStripeSettings((prev) => ({
                  ...prev,
                  defaultMargin: e.target.value,
                }))
              }
              placeholder="20"
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Alert Email</label>
            <p className="text-xs text-muted-foreground mt-1">
              Email address for billing alerts and notifications
            </p>
            <input
              type="email"
              value={stripeSettings.alertEmail}
              onChange={(e) =>
                setStripeSettings((prev) => ({
                  ...prev,
                  alertEmail: e.target.value,
                }))
              }
              placeholder="admin@byteable.ai"
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="auto-charge"
              checked={stripeSettings.autoCharge}
              onChange={(e) =>
                setStripeSettings((prev) => ({
                  ...prev,
                  autoCharge: e.target.checked,
                }))
              }
              className="rounded border-gray-300"
            />
            <label htmlFor="auto-charge" className="text-sm font-medium">
              Auto-charge on threshold
            </label>
            <p className="text-xs text-muted-foreground">
              Automatically charge invoices when usage threshold is reached
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="rounded-lg border p-4">
        <h3 className="text-md font-medium mb-3">Actions</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={saveStripeSettings}
            disabled={stripeLoading}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {stripeLoading ? "Saving..." : "Save Configuration"}
          </button>
          <button
            onClick={testStripeConnection}
            disabled={stripeLoading}
            className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
          >
            {stripeLoading ? "Testing..." : "Test Stripe Connection"}
          </button>
          <button
            onClick={syncStripeProducts}
            disabled={stripeLoading}
            className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
          >
            {stripeLoading ? "Syncing..." : "Sync Products"}
          </button>
        </div>
      </div>
    </div>
  );
}
